"use client";

import { NodeSelection, TextSelection } from "@tiptap/pm/state";
import { EditorContent } from "@tiptap/react";
import { useCallback, useEffect, useRef } from "react";
import { cx } from "@/lib/tailwind-utils";
import { getBodyStyles, getContainerStyles, useEmailComposer } from "./context";
import "./editor/editor.css";
import { findLastTextBlockEnd } from "./editor/node-selection-drag-plugin";
import { GOOGLE_FONTS_CDN_URL } from "./fonts";
interface CanvasProps {
  autoFocus?: boolean;
  readOnly?: boolean;
  className?: string;
}
export function Canvas({
  autoFocus,
  readOnly,
  className
}: CanvasProps) {
  const {
    mode,
    editor,
    styleSettings,
    backgroundSelected,
    setBackgroundSelected
  } = useEmailComposer();
  const bgSelectedRef = useRef(backgroundSelected);
  bgSelectedRef.current = backgroundSelected;
  const mouseDownOnBgRef = useRef(false);
  useEffect(() => {
    if (!editor) return;
    editor.setEditable(!readOnly);
  }, [editor, readOnly]);
  useEffect(() => {
    if (!editor || !autoFocus) return;
    const timer = setTimeout(() => {
      editor.commands.focus();
    }, 100);
    return () => clearTimeout(timer);
  }, [editor, autoFocus]);

  /**
   * Handle mousedown on the canvas wrapper. Clicks inside ProseMirror are
   * handled by the node-selection-drag plugin. But clicks on the container
   * padding (outside ProseMirror) that are visually next to a row should
   * select that row rather than triggering the background selection.
   *
   * We find the row whose vertical range contains the click Y coordinate.
   */
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    mouseDownOnBgRef.current = false;
    if (!editor || e.button !== 0) return;
    const target = e.target as HTMLElement;

    // Ignore events from React portals (e.g. tippy popups for slash
    // commands / mentions). Their DOM target lives on document.body,
    // not inside this canvas element, but React's portal event
    // bubbling still delivers the synthetic event here.
    if (e.currentTarget instanceof HTMLElement && !e.currentTarget.contains(target)) return;

    // Clicks inside ProseMirror are normally handled natively. Exception:
    // the editable is a flex column stretched to fill the composer height, so
    // a click in the empty space *below* the last line lands inside
    // .ProseMirror yet maps to no text position — the browser drops no caret
    // and the click looks dead. In the simple (conversational) composer,
    // catch a click below the last row and drop the caret at the end so you
    // can click anywhere in the box and start typing.
    if (target.closest(".ProseMirror")) {
      if (mode === "simple") {
        let lastRowBottom = -Infinity;
        editor.state.doc.forEach((_node, offset) => {
          const dom = editor.view.nodeDOM(offset);
          if (!(dom instanceof HTMLElement)) return;
          let rect = dom.getBoundingClientRect();
          if (rect.height < 5 && dom.firstElementChild) {
            rect = dom.firstElementChild.getBoundingClientRect();
          }
          if (rect.bottom > lastRowBottom) lastRowBottom = rect.bottom;
        });
        if (e.clientY > lastRowBottom) {
          const textEnd = findLastTextBlockEnd(editor.state.doc);
          if (textEnd !== null) {
            e.preventDefault();
            editor.view.dispatch(editor.state.tr.setSelection(TextSelection.create(editor.state.doc, textEnd)));
            editor.view.focus();
          }
        }
      }
      return;
    }

    // Find which row (if any) the click Y coordinate falls within.
    // Row DOM elements use display:table which can have tiny rects,
    // so also check firstElementChild (the cell) for a better rect.
    const {
      doc
    } = editor.state;
    let matchedRowPos: number | null = null;
    doc.forEach((node, offset_0) => {
      if (matchedRowPos !== null) return;
      const dom_0 = editor.view.nodeDOM(offset_0);
      if (!(dom_0 instanceof HTMLElement)) return;
      let rect_0 = dom_0.getBoundingClientRect();
      if (rect_0.height < 5 && dom_0.firstElementChild) {
        rect_0 = dom_0.firstElementChild.getBoundingClientRect();
      }
      if (e.clientY >= rect_0.top && e.clientY <= rect_0.bottom && e.clientX >= rect_0.left && e.clientX <= rect_0.right) {
        matchedRowPos = offset_0;
      }
    });
    if (matchedRowPos !== null) {
      if (mode === "simple") {
        // Simple mode: place cursor at end of last text block in that row
        e.preventDefault();
        e.stopPropagation();
        const rowPos = matchedRowPos;
        const rowNode = editor.state.doc.nodeAt(rowPos);
        if (rowNode) {
          // Find the last text block inside the row to avoid landing on atom nodes
          let lastTextBlockEnd: number | null = null;
          rowNode.descendants((node_0, pos) => {
            if (node_0.isTextblock) {
              lastTextBlockEnd = rowPos + 1 + pos + node_0.nodeSize - 1;
            }
          });
          if (lastTextBlockEnd !== null) {
            const tr = editor.state.tr.setSelection(TextSelection.create(editor.state.doc, lastTextBlockEnd));
            editor.view.dispatch(tr);
          }
          editor.view.focus();
        }
        return;
      }
      // Block mode: click selects the row
      e.preventDefault();
      e.stopPropagation();
      const tr_0 = editor.state.tr.setSelection(NodeSelection.create(editor.state.doc, matchedRowPos));
      editor.view.dispatch(tr_0);
      editor.view.focus();
      return;
    }

    // Click below all rows. In the simple (conversational) composer, place
    // the caret at the end and focus so a click in the empty area just lets
    // you type — never blur into a background selection (that's block mode).
    const textEnd_0 = findLastTextBlockEnd(editor.state.doc);
    if (mode === "simple") {
      if (textEnd_0 !== null) {
        e.preventDefault();
        e.stopPropagation();
        editor.view.dispatch(editor.state.tr.setSelection(TextSelection.create(editor.state.doc, textEnd_0)));
        editor.view.focus();
      }
      return;
    }

    // Block mode: treat as a background click. Also move the selection to the
    // end of the last text block so a stale NodeSelection doesn't linger.
    if (textEnd_0 !== null) {
      editor.view.dispatch(editor.state.tr.setSelection(TextSelection.create(editor.state.doc, textEnd_0)));
    }
    mouseDownOnBgRef.current = true;
  }, [editor, mode]);
  if (!editor) return null;
  return <div className={cx("email-editor flex flex-col", className)} style={getBodyStyles(styleSettings)} onMouseDown={handleMouseDown} onDoubleClick={e_0 => {
    if (!editor) return;
    const target_0 = e_0.target as HTMLElement;
    if (!(e_0.currentTarget as HTMLElement).contains(target_0)) return;
    if (target_0.closest(".ProseMirror")) return;

    // Double-click below all rows → create a new empty row and focus
    const {
      doc: doc_0,
      schema,
      tr: tr_1
    } = editor.state;
    const rowType = schema.nodes["emailRow"];
    const cellType = schema.nodes["emailCell"];
    const paragraphType = schema.nodes["emailParagraph"];
    if (!rowType || !cellType || !paragraphType) return;
    e_0.preventDefault();
    e_0.stopPropagation();
    const endPos = doc_0.content.size;
    const newRow = rowType.create(null, [cellType.create({
      colSpan: 4
    }, [paragraphType.create()])]);
    tr_1.insert(endPos, newRow);
    tr_1.setSelection(TextSelection.near(tr_1.doc.resolve(endPos + 3)));
    editor.view.dispatch(tr_1);
    editor.view.focus();
  }} onMouseUp={e_1 => {
    // Background click = mousedown AND mouseup both on the background.
    if (!mouseDownOnBgRef.current) return;
    mouseDownOnBgRef.current = false;

    // Verify mouseup is also outside ProseMirror
    const target_1 = e_1.target as HTMLElement;
    if (target_1.closest(".ProseMirror")) return;

    // Deselect any blocks / close bubble menus by moving selection
    // to start of doc (away from any button/image).
    // Must happen BEFORE setBackgroundSelected because the dispatch
    // triggers selectionUpdate which clears backgroundSelected.
    if (editor.state.doc.content.size > 0) {
      const $start = editor.state.doc.resolve(0);
      const tr_2 = editor.state.tr.setSelection(TextSelection.near($start, 1));
      editor.view.dispatch(tr_2);
    }
    editor.commands.blur();
    if (mode === "block") {
      setBackgroundSelected(!bgSelectedRef.current);
    }

    // If there are no rows at all, insert one and focus into it
    const {
      doc: doc_1
    } = editor.state;
    if (doc_1.childCount === 0) {
      const {
        schema: schema_0,
        tr: tr_3
      } = editor.state;
      const rowType_0 = schema_0.nodes["emailRow"];
      const cellType_0 = schema_0.nodes["emailCell"];
      const paragraphType_0 = schema_0.nodes["emailParagraph"];
      if (!rowType_0 || !cellType_0 || !paragraphType_0) return;
      const endPos_0 = doc_1.content.size;
      const newRow_0 = rowType_0.create(null, [cellType_0.create({
        colSpan: 4
      }, [paragraphType_0.create()])]);
      tr_3.insert(endPos_0, newRow_0);
      const newParagraphPos = endPos_0 + 3;
      tr_3.setSelection(TextSelection.near(tr_3.doc.resolve(newParagraphPos)));
      editor.view.dispatch(tr_3);
      editor.view.focus();
    }
  }}>
      {/* Load Google Fonts by real name so inline font-family marks render */}
      {/* eslint-disable-next-line @next/next/no-page-custom-font */}
      <link rel="stylesheet" href={GOOGLE_FONTS_CDN_URL} />
      <style>{`
        .email-editor a { color: ${styleSettings.linkColor}; }
      `}</style>
      <div className="flex flex-1 flex-col" style={getContainerStyles(styleSettings)}>
        <EditorContent editor={editor} className="flex flex-1 flex-col focus:ring-0 focus:border-none" />
      </div>
    </div>;
}